import * as THREE from 'three';
import { ballRadius } from '../sim/world.js';
import { computePocketOpenings, semicircleArc } from './table-rails.js';
import { registerFeltMaterial } from './theme.js';

// 喉部フェルト（bed のクロスと同色）。ラシャ色テーマへ登録して連動させる。
const FELT_MAT = new THREE.MeshStandardMaterial({
  color: 0x1d7a3e,
  roughness: 0.94,
  metalness: 0.0,
  envMapIntensity: 0.22,
});
registerFeltMaterial(FELT_MAT, 'throat');

/**
 * ポケット開口のビジュアル（自前・GPL 非依存）。
 *
 * 実機では、クッションのジョー斜面〜喉部の床までラシャ（緑フェルト）が回り込み、
 * 黒は実際の落とし穴だけになっている。本モジュールは各ポケットに対し:
 *
 *   1. 喉部の緑フェルト: `鼻先A → カット外側角A → カット外側角B → 鼻先B` の四角形。
 *   2. 黒の落とし穴:
 *      - コーナー: カット外側角 A→B を直径とする外向き半円（木枠半円の中身）。
 *      - サイド: 鼻先 A→B を直径とする外向き半円（木枠は直線のまま、喉部内に穴が見える）。
 *
 * z-fighting 回避: 喉部緑・黒穴は bed クロス(z=-R)と同高になりやすいので、わずかに
 * 持ち上げ + polygonOffset でクロスより手前に確定させる（クロス境界の破線対策）。
 */
export const addTablePockets = (scene: THREE.Scene): void => {
  try {
    const openings = computePocketOpenings();
    if (openings.length === 0) {
      console.error('addTablePockets: no pocket openings computed');
      return;
    }

    // 喉部フェルト（モジュール定数 FELT_MAT を使用）。polygonOffset でクロス手前に確定。
    const feltMat = FELT_MAT;
    feltMat.polygonOffset = true;
    feltMat.polygonOffsetFactor = -1;
    feltMat.polygonOffsetUnits = -1;
    const blackMat = new THREE.MeshStandardMaterial({
      color: 0x040404,
      roughness: 0.85,
      metalness: 0.0,
      envMapIntensity: 0.05,
      polygonOffset: true,
      polygonOffsetFactor: -2,
      polygonOffsetUnits: -2,
    });

    const zFelt = -ballRadius + 0.002; // クロスのすぐ上（z-fight 回避）
    const zHole = -ballRadius + 0.004; // 喉部緑のさらに上

    for (const o of openings) {
      // --- 喉部の緑フェルト ---
      const throat = new THREE.Shape();
      throat.moveTo(o.mouthA.x, o.mouthA.y);
      throat.lineTo(o.cutA.x, o.cutA.y);
      throat.lineTo(o.cutB.x, o.cutB.y);
      throat.lineTo(o.mouthB.x, o.mouthB.y);
      throat.closePath();
      const throatMesh = new THREE.Mesh(new THREE.ShapeGeometry(throat), feltMat);
      throatMesh.position.set(0, 0, zFelt);
      throatMesh.receiveShadow = true;
      scene.add(throatMesh);

      // --- 黒の落とし穴 ---
      // コーナー: カット外側角を直径とする半円（木枠半円の中）。
      // サイド: 鼻先を直径とする半円（喉部内に見える穴）。
      const a = o.isCorner ? o.cutA : o.mouthA;
      const b = o.isCorner ? o.cutB : o.mouthB;
      const hole = new THREE.Shape();
      hole.moveTo(a.x, a.y);
      const arc = semicircleArc(a, b, o.center);
      for (const pt of arc) hole.lineTo(pt.x, pt.y);
      hole.lineTo(b.x, b.y);
      hole.closePath();
      const holeMesh = new THREE.Mesh(new THREE.ShapeGeometry(hole), blackMat);
      holeMesh.position.set(0, 0, zHole);
      scene.add(holeMesh);
    }
  } catch (err) {
    console.error('addTablePockets failed:', err);
  }
};
